"use client";

import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";

import { cn } from "./utils";
import { useReadOnlyFields } from "./read-only-fields";

function Select({
  disabled,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
  const readOnlyFields = useReadOnlyFields();
  return <SelectPrimitive.Root data-slot="select" disabled={disabled || readOnlyFields} {...props} />;
}

function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

/**
 * A value longer than its trigger ends in an ellipsis.
 *
 * A trigger is as wide as its column and its label is one line that does not
 * wrap, so wherever the label is the longer of the two, part of it is hidden.
 * The value used to be a flex box that hides what it cannot hold. Its text
 * stands in a box of its own there, which `text-overflow` does not reach, so
 * the label was cut through a letter with nothing to say more follows:
 * "Spojené arabské emi" for a source market on a phone, "CZK — Česká kor"
 * for the property's currency on a tablet with the rail open. (`line-clamp-1`
 * beside it never applied: its `-webkit-box` came before `flex` in the built
 * CSS and lost to it, and a line that does not wrap is never clamped.)
 *
 * Every value in the product is plain text, so plain text is what is ended:
 * `truncate` on the value, which the trigger, a flex row, lays out as a
 * block. A value that holds an element (an icon beside its text) is the row
 * with a gap it has always been, and its text is the caller's to shorten, in
 * a `truncate` span of its own.
 *
 * Two things stay a caller's. A value that has to be read whole is given the
 * width instead, a number that ends in an ellipsis being no number (the
 * rows-per-page picker of Recent Bookings). And a trigger is cut only where
 * something holds it: in a column sized by what stands in it, a long label
 * widens the column, so the row that holds a picker is `min-w-0`. The value
 * itself a caller hands nothing; `select.test.tsx` reads every trigger's
 * source.
 */
function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: "sm" | "default";
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-full items-center justify-between gap-2 rounded-md border bg-input-background px-3 py-2 text-sm whitespace-nowrap transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:truncate *:data-[slot=select-value]:has-[>*]:flex *:data-[slot=select-value]:has-[>*]:items-center *:data-[slot=select-value]:has-[>*]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="size-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

/**
 * The list a select opens is held to the window, and an option it cannot
 * hold on one line wraps.
 *
 * The list is drawn over the page in a box as wide as its longest option,
 * and nothing held that box to the window. Radix moves it left until its left
 * edge is 10px inside the window and can do no more, so a list wider than the
 * window stood past its right edge, where an option's end could be neither
 * read nor scrolled to: a rate plan named by its PMS, with the setting that
 * holds it, by up to 289px on a 320px phone.
 *
 * The list is no wider than the room it has: the window less the 10px Radix
 * keeps free at each side, and the room Radix measures where that is the
 * smaller. The first is stated as a length (`100vw - 20px`) beside Radix's
 * own variable because the variable is set by Radix's first pass over a list
 * already laid out, the pass that also decides whether the list goes under
 * its trigger or over it. Held by the variable alone, a list was one line an
 * option on that pass and wrapped, and grew taller, only after its side was
 * chosen: measured, 11 lists were cut to the room under their trigger and
 * scrolled with room for all of them over it, and 5 kept a scroll button with
 * nothing left to scroll. The scroller's floor (the trigger's width, which is
 * what makes a list of short options as wide as its trigger) is held to the
 * same room, or a trigger wider than the room would hold the scroller wider
 * than the list and cut the check marks off. Both only where the list is
 * placed beside its trigger: one laid over its trigger is sized by Radix.
 * (`100vw` counts a scrollbar the document draws. The shell's document never
 * scrolls, and where a document does, the measured room is the smaller of
 * the two once Radix has measured it.)
 *
 * An option wraps, and is not ended in an ellipsis as a trigger's value is:
 * a value is the option somebody already chose, and an option is read to be
 * chosen, so it has to be read whole. Two plans of one channel differ in
 * their last words, and the setting that holds a plan is named at its end.
 * `SelectItem` breaks a word longer than the list as well (`wrap-anywhere`:
 * a word that only breaks where it would stand out of its line still asks
 * for its whole width, and the list cuts what it cannot hold). A list that
 * fits is as wide as it was and stands where it stood.
 *
 * A caller hands the list no `max-w-*`, `min-w-*` or `overflow-*`, and an
 * option nothing that keeps it on one line; `select.list.test.tsx` reads
 * every list's and every option's source.
 */
function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md",
          position === "popper" &&
            "max-w-[min(var(--radix-select-content-available-width,100vw),calc(100vw-20px))] data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[min(var(--radix-select-trigger-width),var(--radix-select-content-available-width,100vw),calc(100vw-20px))] scroll-my-1",
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm wrap-anywhere outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className,
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
